import { Box, Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  getAnalysisDetailPath,
  getAnalysisVersionPath,
  getAnalyticalDatasetDetailPath,
  getAnalyticalDatasetVersionPath,
  getProjectCohortPath,
  getProjectCohortVersionPath,
  getProjectConceptGroupPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import {
  avKeys,
  libraryAvToVariableDetail,
  type AnalyticalVariableListItem,
  type VariableDetail,
} from '@verantos-dev/feature-analytical-variables';
import {
  CohortModalProvider,
  CopyCohortModal,
  CreateCohortModal,
  DeleteCohortModal,
  ExportCohortModal,
  RenameCohortModal,
  useCohortModalContext,
} from '@verantos-dev/feature-cohort-list';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { useChatPreservingNavigate } from '../hooks/useChatPreservingNavigate';
import { useConceptGroupExport } from '../hooks/useConceptGroupExport';
import { useProjectArtifacts } from '../hooks/useProjectArtifacts';
import type { CreatedAnalyticalDataset } from '../lib/api/useCreateAnalyticalDatasetMutation';
import type {
  ProjectArtifact,
  ProjectArtifactFilterKind,
  ProjectArtifactKind,
  ProjectArtifactScopeFilter,
} from '../types';
import { CreateConceptGroupModal } from './concept-group/components/CreateConceptGroupModal';
import { ConceptGroupExportPill } from './ConceptGroupExportPill';
import { ConceptGroupsExportModal } from './ConceptGroupsExportModal';
import { CreateAnalyticalDatasetDialog } from './CreateAnalyticalDatasetDialog';
import { ProjectArtifactsDataGrid } from './ProjectArtifactsDataGrid';
import {
  ProjectArtifactsToolbar,
  type CreateOption,
  type ProjectArtifactCounts,
  type ProjectArtifactScopeCounts,
} from './ProjectArtifactsToolbar';
import { ProjectArtifactsWelcomeCard } from './ProjectArtifactsWelcomeCard';

const FILTER_QUERY_PARAM = 'kind';
// OR-6615 scope facet URL param.
const SCOPE_QUERY_PARAM = 'scope';

// Shared page-gutter scalar for gen-3 project surfaces. theme.spacing(6)
// = 24px (OR-6123 F9 — Figma 65-10129) — matches CohortDetailHeader /
// DefinitionTab / the AV + dataset detail pages so the left/right gutter is
// visually consistent across the project root and every detail surface. This
// is the sole horizontal inset: the content outlet + panel wrappers add none.
const PAGE_GUTTER = 6;

const FILTER_VALUES: ReadonlyArray<ProjectArtifactFilterKind> = [
  'all',
  'analytical_dataset',
  'cohort',
  'variable',
  'concept_group',
  'analysis',
];

const parseFilter = (raw: string | null): ProjectArtifactFilterKind => {
  if (!raw) return 'all';
  return (FILTER_VALUES as ReadonlyArray<string>).includes(raw)
    ? (raw as ProjectArtifactFilterKind)
    : 'all';
};

const SCOPE_FILTER_VALUES: ReadonlyArray<ProjectArtifactScopeFilter> = [
  'all',
  'project',
  'organization',
];

const parseScopeFilter = (raw: string | null): ProjectArtifactScopeFilter => {
  if (!raw) return 'all';
  return (SCOPE_FILTER_VALUES as ReadonlyArray<string>).includes(raw)
    ? (raw as ProjectArtifactScopeFilter)
    : 'all';
};

// An org-library or Verantos-global asset. Absent scope reads as the
// project's own.
const isOrgScoped = (artifact: ProjectArtifact): boolean =>
  artifact.scope === 'org' || artifact.scope === 'global';

/**
 * Default content for the agent-first project page (`/projects/:projectId`).
 * Renders a time-ordered, type-filterable list of project artifacts assembled
 * client-side from per-kind data sources.
 *
 * The Create dropdown opens kind-specific creation dialogs **inline** (in
 * the project page) rather than routing to sibling list pages — once the
 * user is on the project page, we keep them there. Cohort, concept-group
 * and (behind the `analytical_datasets` flag) analytical-dataset dialogs
 * are reused from their existing feature libs; on success the dataset flow
 * routes straight into the new dataset's detail page. Variable creation is
 * not yet available; its entry stays disabled with an explanatory tooltip.
 *
 * Phase 3 scope — see `DESIGN_OR_5963_AGENT_FIRST_PROJECT_INTEGRATION.md`.
 */
export const ProjectArtifactsListPage = () => {
  return (
    <CohortModalProvider>
      <ProjectArtifactsListPageBody />
    </CohortModalProvider>
  );
};

const ProjectArtifactsListPageBody = () => {
  // Preserve ``?chat=`` when navigating into an artifact-detail route
  // so the active chat sidecar survives the path change. See
  // ``useChatPreservingNavigate`` for the rationale.
  const navigate = useChatPreservingNavigate();
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const safeProjectId = projectId ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [createConceptGroupOpen, setCreateConceptGroupOpen] = useState(false);
  const chatNavigate = useChatPreservingNavigate();
  const [createDatasetOpen, setCreateDatasetOpen] = useState(false);

  // Analysis rows, filter chip, and detail route are all gated on
  // this flag. TODO: cleanup with descriptive_analysis_impl ff
  const descriptiveAnalysisImplEnabled = useFeatureFlagSplit(
    'descriptive_analysis_impl',
    false
  ) as boolean;

  const {
    isCreateCohortModalOpen,
    isDeleteCohortModalOpen,
    isRenameCohortModalOpen,
    isCopyCohortModalOpen,
    isExportCohortModalOpen,
    openCreateCohortModal,
  } = useCohortModalContext();

  // Pure route *pathname* per artifact kind (no query string). The
  // in-app click path threads ``?chat=`` through ``navigate``
  // (``useChatPreservingNavigate``); the anchor ``href`` appends it
  // explicitly via ``getArtifactHref`` below.
  const getArtifactPath = useCallback(
    (artifact: ProjectArtifact): string => {
      switch (artifact.kind) {
        case 'cohort': {
          // Skip the version resolver when we already know the latest version
          // (the manifest returns it with the cohort row). The cohort-builder
          // detail page fetches its own profile on navigation.
          const latestCohortVersionId = artifact.kindSpecific?.latestCohortVersionId as
            | string
            | undefined;
          return latestCohortVersionId
            ? getProjectCohortVersionPath(safeProjectId, artifact.id, latestCohortVersionId)
            : getProjectCohortPath(safeProjectId, artifact.id);
        }
        case 'variable':
          return getProjectVariablePath(safeProjectId, artifact.id);
        case 'concept_group':
          return getProjectConceptGroupPath(safeProjectId, artifact.id);
        case 'analytical_dataset': {
          // Skip the version resolver when the manifest already gives us the
          // latest version (mirrors cohort). Fall back to the profile route —
          // which redirects via the resolver — only when it's absent.
          const latestAnalyticalDatasetVersionId = artifact.kindSpecific
            ?.latestAnalyticalDatasetVersionId as string | null | undefined;
          return latestAnalyticalDatasetVersionId
            ? getAnalyticalDatasetVersionPath(
                safeProjectId,
                artifact.id,
                latestAnalyticalDatasetVersionId
              )
            : getAnalyticalDatasetDetailPath(safeProjectId, artifact.id);
        }
        case 'analysis': {
          // Same shape as analytical_dataset: skip the version resolver when
          // the manifest already gives us the latest version.
          const latestAnalysisVersionId = artifact.kindSpecific?.latestAnalysisVersionId as
            | string
            | null
            | undefined;
          return latestAnalysisVersionId
            ? getAnalysisVersionPath(safeProjectId, artifact.id, latestAnalysisVersionId)
            : getAnalysisDetailPath(safeProjectId, artifact.id);
        }
      }
    },
    [safeProjectId]
  );

  // Anchor ``href`` for the datagrid Name link. Threads the active
  // ``?chat=`` sidecar onto the destination so a Cmd/Ctrl/middle-click
  // that opens the resource in a new tab keeps the sticky chat mounted —
  // matching the plain-click path, which preserves it via
  // ``useChatPreservingNavigate``.
  const getArtifactHref = useCallback(
    (artifact: ProjectArtifact): string => {
      const path = getArtifactPath(artifact);
      const chat = searchParams.get('chat');
      return chat ? `${path}?chat=${chat}` : path;
    },
    [getArtifactPath, searchParams]
  );

  const handleArtifactClick = useCallback(
    (artifact: ProjectArtifact) => {
      if (artifact.kind === 'variable') {
        // Pre-warm the TanStack detail cache from the list snapshot so
        // the detail page paints immediately. Library-sourced AVs
        // attach the full list item under ``kindSpecific.libraryAv``;
        // knowledge-sourced AVs don't carry a list snapshot (the
        // ``av.<slug>`` detail call resolves directly against
        // KNOWLEDGE) and skip the pre-warm. Workaround for the
        // backend asymmetry where the detail endpoint 404s on
        // ``av_denovo`` UUIDs the list returns.
        const libraryAv = artifact.kindSpecific?.libraryAv as
          | AnalyticalVariableListItem
          | undefined;
        if (libraryAv) {
          queryClient.setQueryData<ObjectResponse<VariableDetail>>(
            avKeys.libraryDetail(artifact.id),
            { data: libraryAvToVariableDetail(libraryAv) }
          );
        }
      }
      // Pass the bare pathname — ``navigate`` (chat-preserving) threads
      // the active ``?chat=`` onto it.
      navigate(getArtifactPath(artifact));
    },
    [queryClient, navigate, getArtifactPath]
  );

  // After creating a dataset we already hold the latest version id, so route
  // straight to the versioned detail page and skip the resolver round-trip.
  const handleDatasetCreated = useCallback(
    (profile: CreatedAnalyticalDataset) => {
      const versionId = profile.latestAnalyticalDatasetVersionId;
      navigate(
        versionId
          ? getAnalyticalDatasetVersionPath(
              safeProjectId,
              profile.analyticalDatasetProfileId,
              versionId
            )
          : getAnalyticalDatasetDetailPath(safeProjectId, profile.analyticalDatasetProfileId)
      );
    },
    [navigate, safeProjectId]
  );

  const activeFilter = parseFilter(searchParams.get(FILTER_QUERY_PARAM));
  const activeScope = parseScopeFilter(searchParams.get(SCOPE_QUERY_PARAM));

  const { artifacts, isLoading, isError } = useProjectArtifacts(safeProjectId);

  // A kind whose flag is off is invisible everywhere on this page — the
  // grid, the "all" total, and its own chip (filtered out in the toolbar via
  // `descriptiveAnalysisImplEnabled`). Single gate so those three can't drift.
  // TODO: cleanup with descriptive_analysis_impl ff
  const isKindFlagVisible = useCallback(
    (kind: ProjectArtifactKind) => kind !== 'analysis' || descriptiveAnalysisImplEnabled,
    [descriptiveAnalysisImplEnabled]
  );

  // Analytical datasets fold into the Analysis chip/filter — no chip of
  // their own — while the flag is on; a direct kind match otherwise.
  // TODO: cleanup with descriptive_analysis_impl ff
  const matchesActiveFilter = useCallback(
    (artifact: ProjectArtifact, filter: ProjectArtifactFilterKind): boolean => {
      if (filter === 'all') return true;
      if (artifact.kind === filter) return true;
      return (
        descriptiveAnalysisImplEnabled &&
        filter === 'analysis' &&
        artifact.kind === 'analytical_dataset'
      );
    },
    [descriptiveAnalysisImplEnabled]
  );

  const visibleArtifacts = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    return artifacts.filter((artifact) => {
      if (!isKindFlagVisible(artifact.kind)) return false;
      if (!matchesActiveFilter(artifact, activeFilter)) return false;
      if (activeScope === 'project' && isOrgScoped(artifact)) return false;
      if (activeScope === 'organization' && !isOrgScoped(artifact)) return false;
      if (!trimmed) return true;
      // Match the visible display label AND the internal slug, so AV rows are
      // findable by either their data_element or their slug (e.g. age_gte_70).
      const haystack = `${artifact.name} ${artifact.displayName ?? ''}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [artifacts, activeFilter, activeScope, search, isKindFlagVisible, matchesActiveFilter]);

  // Filter-chip counts are derived synchronously from the already-loaded
  // manifest — there's no separate count endpoint. They are *kind totals*,
  // independent of the active filter and the search box, so the chip numbers
  // stay stable while the user narrows the list. Flag-hidden kinds (see
  // `isKindFlagVisible`) are excluded so "All types" agrees with the grid.
  const counts = useMemo<ProjectArtifactCounts>(() => {
    const tally: ProjectArtifactCounts = {
      all: 0,
      analytical_dataset: 0,
      cohort: 0,
      variable: 0,
      concept_group: 0,
      analysis: 0,
    };
    artifacts.forEach((artifact) => {
      if (!isKindFlagVisible(artifact.kind)) return;
      tally[artifact.kind] += 1;
      tally.all += 1;
    });
    // Analytical datasets fold into the Analysis chip's count while the flag
    // is on — there's no separate chip for their own kind.
    // TODO: cleanup with descriptive_analysis_impl ff
    if (descriptiveAnalysisImplEnabled) {
      tally.analysis += tally.analytical_dataset;
    }
    return tally;
  }, [artifacts, isKindFlagVisible, descriptiveAnalysisImplEnabled]);

  // These count the active kind filter's subset, so the row agrees with the
  // table. Search narrows neither.
  const scopeCounts = useMemo<ProjectArtifactScopeCounts>(() => {
    const tally: ProjectArtifactScopeCounts = { all: 0, project: 0, organization: 0 };
    artifacts.forEach((artifact) => {
      if (!isKindFlagVisible(artifact.kind)) return;
      if (!matchesActiveFilter(artifact, activeFilter)) return;
      tally.all += 1;
      if (isOrgScoped(artifact)) tally.organization += 1;
      else tally.project += 1;
    });
    return tally;
  }, [artifacts, activeFilter, isKindFlagVisible, matchesActiveFilter]);

  // A brand-new project with no artifacts of any kind: swap the filter/search
  // toolbar and the grid body for a welcome message instead of showing a bare
  // "No rows" table with all-zero filter chips. Gated on the loaded/error
  // state so it doesn't flash before the manifest resolves.
  const isEmptyProject = !isLoading && !isError && counts.all === 0;

  const handleFilterChange = useCallback(
    (next: ProjectArtifactFilterKind) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === 'all') {
            updated.delete(FILTER_QUERY_PARAM);
          } else {
            updated.set(FILTER_QUERY_PARAM, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const conceptGroupExport = useConceptGroupExport({
    projectId: safeProjectId,
    artifacts,
    visibleArtifacts,
    activeFilter,
  });

  const handleScopeFilterChange = useCallback(
    (next: ProjectArtifactScopeFilter) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === 'all') {
            updated.delete(SCOPE_QUERY_PARAM);
          } else {
            updated.set(SCOPE_QUERY_PARAM, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const createOptions = useMemo<CreateOption[]>(
    () => [
      {
        id: 'analytical_dataset' satisfies ProjectArtifactKind,
        label: 'Analytical dataset',
        onSelect: () => setCreateDatasetOpen(true),
      },
      {
        id: 'cohort',
        label: 'Cohort',
        onSelect: openCreateCohortModal,
      },
      // No "Variable" entry — there is no standalone project-scoped AV
      // creation flow (AVs are promoted from inside a cohort builder), and
      // the F9 design omits it from the Create menu. The "Variables" filter
      // chip stays so existing AVs remain browsable.
      {
        id: 'concept_group',
        label: 'Concept group',
        // Opens the create modal (name + upload); Create mints a draft and
        // routes to its review.
        onSelect: () => setCreateConceptGroupOpen(true),
      },
    ],
    [openCreateCohortModal]
  );

  return (
    <Box
      data-testid="project-artifacts-list-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          // Anchors the pill over the list, not the viewport.
          position: 'relative',
          paddingX: PAGE_GUTTER,
          // Top inset matches the row gap below (2) rather than the full
          // page gutter (6/24px) — that larger value read fine when Create
          // sat inside a bordered card right at this boundary, but floats
          // as visible dead space now that it's an unbordered row.
          paddingTop: 2,
          paddingBottom: PAGE_GUTTER,
        }}
      >
        {isLoading ? (
          // Plain loading skeleton — The toolbar/grid or the welcome card
          // only mounts once we know which one actually applies.
          <Box
            data-testid="project-artifacts-loading-skeleton"
            sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: `${borderRadius.sm}px` }}
            />
            <Skeleton
              variant="rectangular"
              sx={{ flex: 1, minHeight: 0, borderRadius: `${borderRadius.md}px` }}
            />
          </Box>
        ) : isEmptyProject ? (
          <>
            <ProjectArtifactsToolbar
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
              search={search}
              onSearchChange={setSearch}
              createOptions={createOptions}
              counts={counts}
              countsLoading={isLoading}
              showFilters={false}
              showSearch={false}
              bordered={false}
              descriptiveAnalysisImplEnabled={descriptiveAnalysisImplEnabled}
            />
            <ProjectArtifactsWelcomeCard />
          </>
        ) : (
          <>
            {/* Create sits above the card */}
            <ProjectArtifactsToolbar
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
              search={search}
              onSearchChange={setSearch}
              createOptions={createOptions}
              counts={counts}
              countsLoading={isLoading}
              showFilters={false}
              showSearch={false}
              bordered={false}
              descriptiveAnalysisImplEnabled={descriptiveAnalysisImplEnabled}
            />
            <ProjectArtifactsDataGrid
              artifacts={visibleArtifacts}
              isLoading={isLoading}
              isError={isError}
              projectId={safeProjectId}
              getArtifactHref={getArtifactHref}
              onActivate={handleArtifactClick}
              selection={conceptGroupExport.gridSelection}
              header={
                <ProjectArtifactsToolbar
                  activeFilter={activeFilter}
                  onFilterChange={handleFilterChange}
                  search={search}
                  onSearchChange={setSearch}
                  createOptions={createOptions}
                  counts={counts}
                  countsLoading={isLoading}
                  showCreate={false}
                  activeScopeFilter={activeScope}
                  onScopeFilterChange={handleScopeFilterChange}
                  scopeCounts={scopeCounts}
                  descriptiveAnalysisImplEnabled={descriptiveAnalysisImplEnabled}
                />
              }
            />
            {/* Floats over the grid: selection is transient, and the list
                underneath is what the user reads while building it. */}
            {conceptGroupExport.pill && <ConceptGroupExportPill {...conceptGroupExport.pill} />}
          </>
        )}
      </Box>
      {conceptGroupExport.exportModal && (
        <ConceptGroupsExportModal {...conceptGroupExport.exportModal} />
      )}
      {isCreateCohortModalOpen && <CreateCohortModal />}
      {isDeleteCohortModalOpen && <DeleteCohortModal />}
      {isRenameCohortModalOpen && <RenameCohortModal />}
      {isCopyCohortModalOpen && <CopyCohortModal />}
      {isExportCohortModalOpen && <ExportCohortModal />}
      {createDatasetOpen && (
        <CreateAnalyticalDatasetDialog
          projectId={safeProjectId}
          onClose={() => setCreateDatasetOpen(false)}
          onCreated={handleDatasetCreated}
        />
      )}
      {createConceptGroupOpen && (
        <CreateConceptGroupModal
          projectId={safeProjectId}
          onClose={() => setCreateConceptGroupOpen(false)}
          // Straight to the canonical asset route when the stable id exists —
          // hopping through the preview shim (which forwards there anyway)
          // flashed an extra loading state. The modal prefetches the
          // destination's data before firing this.
          onCreated={({ previewId, conceptGroupId }) =>
            chatNavigate(
              conceptGroupId
                ? `/projects/${safeProjectId}/concept-group/${conceptGroupId}`
                : `/projects/${safeProjectId}/concept-group-preview/${previewId}`
            )
          }
        />
      )}
    </Box>
  );
};

export default ProjectArtifactsListPage;
